import { Grid, Paper, Table, TableBody, TableCell, TableRow } from '@mui/material'
import { WarningAmber } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { selectUser } from 'store/selectors/user'
import { getLongFormatDate } from 'utils/dates'

const getTimeRemainingLabel = (expiryDate, language) => {
  const now = new Date()
  const expiry = new Date(expiryDate)

  if (Number.isNaN(expiry.getTime())) {
    return null
  }

  if (expiry <= now) {
    return language.startsWith('fr') ? '0 mois' : '0 months'
  }

  let years = expiry.getFullYear() - now.getFullYear()
  let months = expiry.getMonth() - now.getMonth()

  if (expiry.getDate() < now.getDate()) {
    months -= 1
  }

  if (months < 0) {
    years -= 1
    months += 12
  }

  const isFrench = language.startsWith('fr')
  const yearLabel = isFrench ? (years === 1 ? 'an' : 'ans') : years === 1 ? 'year' : 'years'
  const monthLabel = isFrench ? 'mois' : months === 1 ? 'month' : 'months'

  const parts = []
  if (years > 0) {
    parts.push(`${years} ${yearLabel}`)
  }
  if (months > 0 || years === 0) {
    parts.push(`${months} ${monthLabel}`)
  }

  return parts.join(isFrench ? ' et ' : ' and ')
}

const UserManagePlan = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)
  const { billingPlan, organization, role } = useSelector(selectUser)

  const notAvailable = t.user.profile.notAvailable
  const expiryDate = organization?.expiryDate
    ? getLongFormatDate(organization.expiryDate)
    : notAvailable
  const timeRemaining = organization?.expiryDate
    ? getTimeRemainingLabel(organization.expiryDate, i18n.language)
    : null
  const isRenewalSoon = organization?.expiryDate
    ? (() => {
        const today = new Date()
        const expiry = new Date(organization.expiryDate)
        if (Number.isNaN(expiry.getTime())) {
          return false
        }
        const diffInDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
        return diffInDays > 0 && diffInDays <= 60
      })()
    : false

  return (
    <Grid className="user-manage-plan">
      <h3>{t.user.profile.managePlanTitle}</h3>
      <p>{t.user.profile.managePlanDescription}</p>
      {isRenewalSoon && (
        <div className="renewal-banner">
          <WarningAmber fontSize="small" />
          <span>{t.user.profile.renewalSoonBanner}</span>
        </div>
      )}
      <Paper className="current-plan">
        <Grid container>
          <Grid flexGrow={1} xs={12} md={8}>
            <h4>{t.user.profile.currentPlan}</h4>
            <p className="plan-type">{role}</p>
            <p className="plan-length">
              {t.user.profile.planSubscription.replace('{billingPlan}', billingPlan || '')}
            </p>
          </Grid>
          <Grid xs={12} md={4} className="plan-expiry">
            <h4>{t.user.profile.expires}</h4>
            <p className="expiry-date">{expiryDate}</p>
            {timeRemaining !== null && (
              <p className="remaining-days">
                {t.user.profile.timeRemaining.replace('{timeRemaining}', timeRemaining)}
              </p>
            )}
          </Grid>
        </Grid>
      </Paper>
      <Table className="plan-details">
        <TableBody>
          <TableRow>
            <TableCell>{t.user.profile.organization}</TableCell>
            <TableCell>{organization?.name}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t.user.profile.planType}</TableCell>
            <TableCell>{role}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t.user.profile.planLength}</TableCell>
            <TableCell>{billingPlan}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t.user.profile.memberSince}</TableCell>
            <TableCell>
              {organization?.memberSince
                ? getLongFormatDate(organization.memberSince)
                : notAvailable}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t.user.profile.expiresOn}</TableCell>
            <TableCell>{expiryDate}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Grid>
  )
}

UserManagePlan.tabPath = 'manage-plan'
UserManagePlan.getTabName = t => t.user.profile.managePlanTitle

export default UserManagePlan
